import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PartyPopper, Eye } from "lucide-react";
import { EmailSampleBar } from "./EmailSampleBar";
import { getVzGoalEmailsConfig, updateVzGoalEmailsConfig, previewVzGoalEmails, getVzGoalEmailsLog } from "@/lib/verizonSalesApi";

const Row = ({ e, preview }) => (
  <div className="flex justify-between gap-2 border-b border-slate-100 py-1.5 text-xs flex-wrap">
    <span><b className="text-navy">{e.store.label}</b> · {e.dealer_email}</span>
    <span className="text-slate-500">
      {e.store.kpis ? `${e.store.kpis.join(", ")} goal hit` : `${Math.round(e.store.acts)} / ${Math.round(e.goal_acts)} goal`} · {e.month}
      {!preview && <> · <b className={e.status === "sent" ? "text-emerald-600" : "text-red-600"}>{e.status}</b></>}
    </span>
  </div>
);

export const GoalEmailsPanel = () => {
  const [enabled, setEnabled] = useState(null);
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => {
    getVzGoalEmailsConfig().then((c) => setEnabled(c.enabled)).catch(() => toast.error("Failed to load goal email settings"));
    getVzGoalEmailsLog().then(setLog).catch(() => {});
  }, []);
  if (enabled === null) return null;

  const toggle = async (v) => {
    try { await updateVzGoalEmailsConfig(v); setEnabled(v); toast.success(v ? "Goal achieved emails turned ON" : "Goal achieved emails turned OFF"); }
    catch { toast.error("Save failed"); }
  };
  const doPreview = async () => {
    try { setPreview(await previewVzGoalEmails()); } catch { toast.error("Preview failed"); }
  };

  return (
    <Card className="border-slate-200" data-testid="vz-goal-emails-panel">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><PartyPopper className="w-4 h-4 text-amber-500" /> Goal achieved emails</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">After every Verizon sync (and whenever a goal is saved), dealers get one congratulations email whenever a store reaches a new goal on any of the 8 Verizon KPIs (Activations, Upgrades, Edge, Tablets, FWA, TWP+, Top Ups, AutoPay). The email shows the full scorecard with every goal reached in green. Each KPI is celebrated once per store per month; goals come from Store Goals.</p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={toggle} data-testid="vz-goal-emails-switch" />
            <span className="text-sm font-medium">{enabled ? "Goal emails are ON" : "Goal emails are OFF"}</span>
          </div>
          <Button variant="outline" onClick={doPreview} data-testid="vz-goal-emails-preview-btn"><Eye className="w-4 h-4 mr-1" /> Preview pending emails</Button>
        </div>
        <EmailSampleBar kind="goal" />
        {preview && (
          <div data-testid="vz-goal-emails-preview">
            <div className="text-xs font-semibold text-slate-600 mb-1">{preview.skipped || `${preview.emails.length} congratulation email(s) would be sent (preview only)`}</div>
            {preview.emails.map((e) => <Row key={e.id} e={e} preview />)}
          </div>
        )}
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Sent congratulations</div>
          {log.length ? <div data-testid="vz-goal-emails-log">{log.map((e) => <Row key={e.id} e={e} />)}</div> : <p className="text-xs text-slate-500">None sent yet.</p>}
        </div>
      </CardContent>
    </Card>
  );
};
